import type { StepVideo } from "./task-video-client";
import dayjs from "dayjs";
import { amountToMinor } from "./finance-domain";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { assertTask, type TaskPayload, type TaskType, type Agent } from "./domain";
import { businessTaskTypes, taskBusiness, type TaskBusiness } from "./business";
dayjs.extend(utc);
dayjs.extend(timezone);
export interface Values {
  title: string;
  task_type: TaskType;
  description: string;
  description_title?: string;
  rules_title?: string;
  review_rules_title?: string;
  category: string;
  platform: "ios" | "web" | "all";
  price: number;
  quantity: number;
  timeout: number;
  completion_screenshot_count: number;
  submission_requirements: string;
  rules: string;
  review_rules: string;
  remarks: string;
  icon_url: string;
  tags: string[];
  dates?: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  steps_mode?: "default" | "custom";
  steps: {
    id?: string;
    title: string;
    description: string;
    link?: string;
    image?: string;
    images?: string[];
    videos?: StepVideo[];
    links?: { label: string; url: string }[];
  }[];
  change_note: string;
  app_id: number;
  bundle_id: string;
  app_name: string;
  app_version: string;
  app_price: number;
  app_artwork_url: string;
  app_developer_name: string;
  search_keywords: string[];
  keyword_quotas?: { keyword: string; target_count: number }[];
  normal_app_id?: number;
  normal_keyword?: string;
  required_screenshots?: {
    key: string;
    title: string;
    description: string;
    required: boolean;
  }[];
  message_import?: { id: number; count: number };
}
export function fromTask(p: TaskPayload): Partial<Values> {
  return {
    ...p,
    price: p.reward_amount_minor / 100,
    quantity: p.max_assignments,
    timeout: p.assignment_timeout_seconds / 60,
    dates:
      p.start_at || p.end_at
        ? [
            p.start_at ? dayjs(p.start_at).tz("Asia/Shanghai") : null,
            p.end_at ? dayjs(p.end_at).tz("Asia/Shanghai") : null,
          ]
        : undefined,
    steps_mode: p.steps_mode || (p.steps.length ? "custom" : "default"),
    steps: (p.steps_mode === "default" ? [] : p.steps).map((s) => ({
      id: s.id,
      title: s.title,
      description: s.description,
      links: s.links || [],
      images: s.images || [],
      ...(s.videos?.length ? { videos: s.videos } : {}),
      link: s.links?.[0]?.url,
      image: s.images?.[0],
    })),
    normal_app_id: p.task_type === "normal" ? p.download_app?.id : undefined,
    normal_keyword:
      p.task_type === "normal"
        ? p.download_app?.search_keywords?.[0]
        : undefined,
    app_id: p.download_app?.id,
    bundle_id: p.download_app?.bundle_id,
    app_name: p.download_app?.name,
    app_version: p.download_app?.version,
    app_price: p.download_app?.price,
    app_artwork_url: p.download_app?.artwork_url,
    app_developer_name: p.download_app?.developer_name,
    search_keywords: p.download_app?.search_keywords || [],
    keyword_quotas: p.download_app?.search_keyword_quotas?.map(q => ({ keyword: q.keyword, target_count: q.target_count })) || [],
    message_import: p.message_config?.import_id ? { id: p.message_config.import_id, count: p.message_config.import_count || 0 } : undefined,
  };
}
export function defaultTaskType(agent: Agent, business: TaskBusiness): TaskType | undefined {
  return businessTaskTypes(agent, business)[0];
}
export function assertBusinessTaskType(type: TaskType, business: TaskBusiness, allowed: TaskType[], previous?: TaskType) {
  if (taskBusiness(type) !== business || (previous && taskBusiness(previous) !== business))
    throw new Error("任务不能在 App 推广与 iMessage 信息业务之间转换");
  if (!allowed.includes(type)) throw new Error("当前账号未开通此任务类型");
}
export function payloadFromValues(
  v: Values,
  previous?: TaskPayload,
  validate = true,
  scope?: { business: TaskBusiness; allowed: TaskType[]; requireReview?: boolean },
): TaskPayload {
  if (scope) assertBusinessTaskType(v.task_type, scope.business, scope.allowed, previous?.task_type);
  const payload: TaskPayload = {
    ...previous,
    title: (v.title || "").trim(),
    task_type: v.task_type,
    description: (v.description || "").trim(),
    description_title: v.description_title || "",
    rules_title: v.rules_title || "",
    review_rules_title: v.review_rules_title || "",
    category: v.category || "experience",
    platform: !previous && taskBusiness(v.task_type) === "promotion" ? "ios" : v.platform,
    market_id: "CN",
    country_code: "CN",
    currency: "CNY",
    reward_amount_minor: amountToMinor(v.price) ?? 0,
    max_assignments: v.task_type === "download" ? (v.keyword_quotas || []).reduce((sum, q) => sum + (q.target_count || 0), 0) : v.task_type === "message_send" ? (v.message_import?.id ? v.message_import.count : previous?.max_assignments || 0) : v.quantity || 0,
    assignment_timeout_seconds: Math.round((v.timeout || 0) * 60),
    require_review: v.task_type === "message_send" ? true : scope?.requireReview ?? previous?.require_review ?? true,
    icon_url: v.icon_url || "",
    tags: v.tags || [],
    rules: v.rules || "",
    review_rules: v.review_rules || "",
    required_screenshots: ["normal", "other_normal"].includes(v.task_type) ? v.required_screenshots || [] : [],
    completion_screenshot_count: ["normal", "other_normal"].includes(v.task_type) ? v.completion_screenshot_count : 0,
    submission_requirements: v.submission_requirements || "",
    remarks: v.remarks || "",
    steps_mode: v.steps_mode === "default" || !v.steps?.length ? "default" : "custom",
    steps: (v.steps_mode === "default" ? [] : v.steps || []).map((s, i) => ({
      id: s.id || `step-${i + 1}`,
      order: i + 1,
      title: (s.title || "").trim(),
      description: (s.description || "").trim(),
      ...(s.videos?.length ? {videos: s.videos} : {}),
      images: s.images !== undefined ? s.images.filter(Boolean) :
        previous?.steps.some(old => old.id === s.id) && s.image === previous.steps.find(old => old.id === s.id)?.images?.[0]
          ? previous.steps.find(old => old.id === s.id)!.images
          : s.image
            ? [s.image]
            : [],
      links: s.links !== undefined ? s.links.map(link => ({ label: link.label || "", url: (link.url || "").trim() })) :
        previous?.steps.some(old => old.id === s.id) && s.link === previous.steps.find(old => old.id === s.id)?.links?.[0]?.url
          ? previous.steps.find(old => old.id === s.id)!.links
          : s.link
            ? [{ url: s.link, label: "任务链接" }]
            : [],
    })),
    start_at: v.dates?.[0]?.tz("Asia/Shanghai", true).toISOString() || null,
    end_at: v.dates?.[1]?.tz("Asia/Shanghai", true).toISOString() || null,
  };
  // Proof/review fields are platform-owned and are no longer editable here.
  if (scope) {
    const imageTask = ["normal", "other_normal"].includes(v.task_type);
    payload.completion_screenshot_count = imageTask ? previous?.completion_screenshot_count || 0 : 0;
    payload.required_screenshots = imageTask ? previous?.required_screenshots || [] : [];
    payload.submission_requirements = previous?.submission_requirements || "";
    payload.review_rules = previous?.review_rules || "";
    payload.review_rules_title = previous?.review_rules_title || "";
  }
  if (scope) {
    payload.assignment_timeout_seconds = previous?.assignment_timeout_seconds || 0;
    payload.rules = previous?.rules || "";
    payload.rules_title = previous?.rules_title || "";
  }
  delete payload.download_app;
  delete payload.message_config;
  if (v.task_type === "download")
    payload.download_app = {
      ...previous?.download_app,
      ...(previous?.task_type === "download" ? previous.download_app : {}),
      id: v.app_id || 0,
      bundle_id: v.bundle_id || "",
      name: v.app_name || "",
      version: v.app_version || "",
      price: v.app_price || 0,
      artwork_url: v.app_artwork_url || "",
      developer_name: v.app_developer_name || "",
      search_keywords: (v.keyword_quotas || []).map(q => (q.keyword || "").trim()),
      search_keyword_quotas: (v.keyword_quotas || []).map(q => ({
        keyword: (q.keyword || "").trim(), target_count: q.target_count || 0,
        completed_count: 0,
      })),
    };
  if (v.task_type === "normal" && (v.normal_app_id != null || v.normal_keyword?.trim()))
    payload.download_app = {
      id: v.normal_app_id || 0,
      bundle_id: "",
      name: "",
      version: "",
      price: 0,
      artwork_url: "",
      developer_name: "",
      search_keywords: v.normal_keyword?.trim() ? [v.normal_keyword.trim()] : [],
      search_keyword_quotas: [],
    };
  if (v.task_type === "normal" && scope?.business === "promotion") {
    // This optional platform configuration is no longer part of the merchant form.
    // Retain executable existing settings; discard abandoned partial drafts so
    // removed inputs cannot create an unfixable hidden validation error.
    const app = previous?.task_type === "normal" ? previous.download_app : undefined;
    delete payload.download_app;
    if (app && Number.isSafeInteger(app.id) && app.id > 0 && app.search_keywords?.length === 1 && app.search_keywords[0]?.trim() && [...app.search_keywords[0].trim()].length <= 100)
      payload.download_app = app;
  }
  if (v.task_type === "message_send")
    payload.message_config = {
      ...previous?.message_config,
      import_id: v.message_import?.id,
      import_count: v.message_import?.count,
      manual_approval_required: true,
    };
  return validate ? assertTask(payload) : payload;
}

export function distributeQuotas(keywords: string[], quantity: number) {
  return keywords.map((keyword, i) => ({ keyword, target_count: Math.floor(quantity / keywords.length) + (i < quantity % keywords.length ? 1 : 0) }));
}
